import { getSapC4cBaseUrl } from '../../utils/config';
import { logger } from '../../utils/logger';

function normalizeOrigin(value: string): string | null {
  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
}

/**
 * Origins allowed to send CTI dial postMessages into the widget.
 * Always includes the configured SAP C4C base URL when set.
 */
export function getAllowedCtiMessageOrigins(): Set<string> {
  const origins = new Set<string>();
  const sapBase = getSapC4cBaseUrl()?.trim();
  if (sapBase) {
    const origin = normalizeOrigin(sapBase);
    if (origin) {
      origins.add(origin);
    }
  }
  return origins;
}

/** Target origin for postMessage to the SAP parent (falls back to '*' only if unset). */
export function getSapPostMessageTargetOrigin(): string {
  const allowed = getAllowedCtiMessageOrigins();
  if (allowed.size === 1) {
    return [...allowed][0];
  }
  if (allowed.size > 1) {
    return [...allowed][0];
  }
  return '*';
}

/**
 * Post to the SAP CTI host. Posts to `window.parent` only, matching SAP's
 * documented widget integration pattern — the immediate parent frame is
 * responsible for relaying further up if it's nested deeper. Also posting
 * directly to `window.top` caused duplicate delivery (shell relays the
 * parent message AND receives our direct one), which opened two Agent
 * Desktop tabs per action.
 */
export function postToSapHost(message: unknown): void {
  const targetOrigin = getSapPostMessageTargetOrigin();
  window.parent.postMessage(message, targetOrigin);
}

export function isAllowedCtiMessageOrigin(origin: string): boolean {
  const allowed = getAllowedCtiMessageOrigins();

  // Dev / misconfig: no SAP base URL configured — allow all (same as prior behavior).
  if (allowed.size === 0) {
    logger.debug('[SAP CTI] No sapC4cBaseUrl configured; accepting message origin', { origin });
    return true;
  }

  return allowed.has(origin);
}
